import React, { useState } from 'react';
import { FileText, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE_RAL = `RAL disponibles : 1001, 1013, 1014, 1015, 1019, 1024, 1247, 2004,
3003, 3004, 3009, 3011, 3012, 5003, 5008, 5009, 5014,
6005, 6006, 6019, 6021,
7001, 7004, 7005, 7006, 7010, 7011, 7012, 7013, 7015, 7016, 7021, 7022, 7023, 7024, 7030, 7031, 7032, 7033, 7034, 7035, 7036, 7037, 7038, 7039, 7040, 7042, 7043, 7044, 7046, 7047,
8003, 8004, 8011, 8012, 8014, 8016, 8017, 8019, 8022, 8025, 8028,
9001, 9002, 9003, 9005, 9010, 9016, 9018.`;

const initial = {
  name: '',
  email: '',
  phone: '',
  company: '',
  product_type: 'Tablette sur mesure',
  material: '',
  ral_color: '',
  developpe: '',
  length: '',
  quantity: '',
  message: '',
  plan_url: '',
};

const Devis = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/devis`, form);
      setSent(true);
      toast.success('Votre demande de devis a bien été envoyée');
      setForm(initial);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white min-h-screen py-20">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Demande reçue !</h1>
          <p className="text-gray-600 mb-8">
            Merci pour votre demande. Notre équipe l'étudie et revient vers vous sous 48 h ouvrées
            avec un devis personnalisé.
          </p>
          <button
            onClick={() => setSent(false)}
            className="bg-[#E42313] text-white px-6 py-3 rounded hover:bg-[#C11A0F] font-semibold"
          >
            Faire une nouvelle demande
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <section className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <FileText className="w-12 h-12 text-[#E42313] mx-auto mb-4" />
          <h1 className="text-5xl font-bold mb-3">Devis sur mesure</h1>
          <p className="text-xl text-gray-300">
            Tablettes, coudières, reprises de façade, pliage alu ou acier sur plan
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-amber-50 border-l-4 border-amber-400 p-5 mb-8 rounded">
          <h3 className="font-bold text-amber-900 mb-2">Ce que nous savons faire sur mesure</h3>
          <ul className="text-sm text-amber-900 space-y-1 list-disc list-inside">
            <li>Tablettes pour coudières, reprises de façade</li>
            <li>Couvertines développé 300 / 500 / 750 mm (hors standard 375 mm)</li>
            <li>Cornières à la taille souhaitée</li>
            <li>Toutes pièces de pliage aluminium (1 / 1,5 mm) ou acier (0,75 mm)</li>
            <li>Longueur maximum par pièce : 3 m</li>
          </ul>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-gray-200 rounded-lg p-8 space-y-6"
          data-testid="devis-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium mb-1">Nom *</label>
              <input
                data-testid="devis-name"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Société</label>
              <input
                value={form.company}
                onChange={(e) => set('company', e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                data-testid="devis-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Téléphone *</label>
              <input
                data-testid="devis-phone"
                required
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
          </div>

          <hr />

          <div>
            <label className="block text-sm font-medium mb-1">Type de produit *</label>
            <select
              data-testid="devis-product-type"
              value={form.product_type}
              onChange={(e) => set('product_type', e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
            >
              <option>Tablette sur mesure</option>
              <option>Coudière / reprise de façade</option>
              <option>Couvertine développé spécial</option>
              <option>Cornière sur mesure</option>
              <option>Autre pliage alu / acier</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium mb-1">Matière / épaisseur</label>
              <select
                value={form.material}
                onChange={(e) => set('material', e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              >
                <option value="">— Choisir —</option>
                <option value="Aluminium 1 mm">Aluminium 1 mm</option>
                <option value="Aluminium 1,5 mm">Aluminium 1,5 mm</option>
                <option value="Acier 0,75 mm">Acier 0,75 mm</option>
                <option value="Autre">Autre / à préciser</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Couleur / RAL</label>
              <input
                value={form.ral_color}
                onChange={(e) => set('ral_color', e.target.value)}
                placeholder="ex : RAL 7016"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Développé (mm)</label>
              <input
                value={form.developpe}
                onChange={(e) => set('developpe', e.target.value)}
                placeholder="ex : 500 mm"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Longueur totale</label>
              <input
                value={form.length}
                onChange={(e) => set('length', e.target.value)}
                placeholder="ex : 12 m"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Quantité / nombre de pièces</label>
              <input
                value={form.quantity}
                onChange={(e) => set('quantity', e.target.value)}
                placeholder="ex : 6 pièces de 2 m"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description du projet *</label>
            <textarea
              data-testid="devis-message"
              required
              rows={5}
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
              placeholder="Détaillez votre besoin : dimensions précises, chantier, délai souhaité…"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Lien vers un plan / croquis (optionnel)</label>
            <input
              value={form.plan_url}
              onChange={(e) => set('plan_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
            />
            <p className="text-xs text-gray-500 mt-1">
              Vous pouvez aussi envoyer votre plan par mail à contact@neopliage.fr
            </p>
          </div>

          <details className="text-sm text-gray-600 bg-gray-50 rounded p-4">
            <summary className="cursor-pointer font-semibold text-gray-800">
              Liste complète des RAL disponibles
            </summary>
            <p className="mt-2 whitespace-pre-line">{AVAILABLE_RAL}</p>
          </details>

          <button
            data-testid="devis-submit"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#E42313] text-white py-4 rounded font-semibold hover:bg-[#C11A0F] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Envoi…' : 'Envoyer ma demande de devis'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Devis;
